import { Error } from "@mui/icons-material";
import { Box, Collapse, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import WandIcon from "../../../../../../../../assets/Fibo/icons/WandIcon";
import BriaAccordion from "../../../../../../../../components/common/BriaAccordion/BriaAccordion";
import BriaInput from "../../../../../../../../components/common/BriaInput/BriaInput";
import StoppableButton from "../../../../../../../../components/common/StoppableButton/StoppableButton";
import { useAppStore } from "../../../../../../../../hooks/useStores";
import { ActiveTab } from "../../VideoEraserPopup";
import styles from "../VideoEraserPopupConfig.module.scss";

type VideoEraserPromptProps = {
	activeTab: ActiveTab | undefined;
	setActiveTab: (tab: ActiveTab | undefined) => void;
};

const VideoEraserPrompt = ({ activeTab, setActiveTab }: VideoEraserPromptProps) => {
	const { t } = useTranslation("video_editing", { keyPrefix: "lab.config.tools.erase.popup.config.prompt" });
	const { videoGenerationStore } = useAppStore();
	const prompt = videoGenerationStore.segmentConfig.prompt;

	const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		videoGenerationStore.handleSegmentConfigChange("prompt", e.target.value);
	};

	const generateMask = async () => {
		await videoGenerationStore.generateMaskByPrompt();
	};

	return (
		<BriaAccordion
			expanded={activeTab === "prompt"}
			onChange={(_, expanded: boolean) => setActiveTab(expanded ? "prompt" : undefined)}
			accordionTitle={
				<Box className={styles.accordionTitleContainer}>
					<WandIcon className={styles.icon} />
					<Typography className={styles.accordionTitleText}>{t("label")}</Typography>
				</Box>
			}
			titleClass={styles.accordionTitle}
			useSecondaryIcons
		>
			<Box className={styles.content}>
				<BriaInput
					value={prompt}
					onChange={handlePromptChange}
					onEnterPress={generateMask}
					className={styles.textField}
					InputProps={{ className: styles.inputBase }}
					multiline
					minRows={7}
					placeholder={t("placeholder")}
				/>
				<Collapse in={!!videoGenerationStore.errors.generateMask}>
					<Box className={styles.errorMessageContainer}>
						<Error />
						<Typography className={styles.errorMessage}>
							{videoGenerationStore.errors.generateMask?.message}
						</Typography>
					</Box>
				</Collapse>
				<StoppableButton
					condition={videoGenerationStore.isGeneratingMask}
					onStopButtonClick={() => videoGenerationStore.abortVideoSegmentation()}
					onButtonClick={generateMask}
					buttonLabel={t("generateButton")}
					stopButtonLabel={t("stopGenerate")}
					disabled={!prompt}
				/>
			</Box>
		</BriaAccordion>
	);
};

export default observer(VideoEraserPrompt);
